import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMinutes, bucketFor, EFFORT_META } from './choreEffort';

const PRIORITY_COLORS = { 0: 'var(--text-muted)', 1: 'var(--warning-500)', 2: 'var(--danger-500)' };

function ChoreCard({
  chore, categories, rooms = [], expanded, onToggleExpand,
  onClaim, onUnclaim, onComplete, onReopen, onDelete, onEdit,
  onToggleChecklistItem, onAddChecklistItem, onDeleteChecklistItem, onAddGoal, goals,
  onAssign, members
}) {
  const { t, i18n } = useTranslation();
  const [newItemTitle, setNewItemTitle] = useState('');
  const [showGoalPicker, setShowGoalPicker] = useState(false);
  const [showAssignPicker, setShowAssignPicker] = useState(false);

  const goalBucketLabels = {
    0: t('chores.buckets.today'), 1: t('chores.buckets.tomorrow'),
    2: t('chores.buckets.thisWeek'), 3: t('chores.buckets.thisMonth'),
    4: t('chores.buckets.later')
  };

  const checklistItems = chore.checklistItems?.$values || chore.checklistItems || [];
  const completedItems = checklistItems.filter(ci => ci.isCompleted).length;
  const totalItems = checklistItems.length;
  const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
  const isOverdue = chore.dueDate && new Date(chore.dueDate) < startOfToday && chore.status !== 2;
  const isDueSoon = chore.dueDate && !isOverdue && chore.status !== 2 &&
    (new Date(chore.dueDate) - new Date()) < 2 * 24 * 60 * 60 * 1000;

  const category = categories.find(c => c.id === chore.categoryId);
  const room = rooms.find(r => r.id === chore.roomId);
  const roomLabel = room ? ((i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn) : null;
  const effortBucket = bucketFor(chore.estimatedMinutes);
  const hasGoal = goals?.some(g => (g.choreId || g.chore?.id) === chore.id);

  // The API preserves object references, so a repeated user can arrive as a
  // {$ref} stub with no name. Fall back to the member list before giving up.
  const nameFor = (userId, embedded) =>
    embedded?.name || members?.find(m => m.userId === userId)?.name || t('chores.card.someone');
  const assigneeName = chore.claimedByUserId
    ? nameFor(chore.claimedByUserId, chore.claimedByUser)
    : null;
  const wasAssigned = Boolean(chore.assignedByUserId);
  const assignerName = wasAssigned ? nameFor(chore.assignedByUserId, chore.assignedByUser) : null;

  const handleAddItem = (e) => {
    e.preventDefault();
    if (newItemTitle.trim()) {
      onAddChecklistItem(chore.id, newItemTitle.trim());
      setNewItemTitle('');
    }
  };

  return (
    <div className={`chore-card ${isOverdue ? 'chore-card-overdue' : ''} ${isDueSoon ? 'chore-card-due-soon' : ''} ${chore.status === 2 ? 'chore-card-completed' : ''}`}>
      <div className="chore-card-header" onClick={onToggleExpand} role="button" aria-expanded={expanded} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleExpand(); } }}>
        <div className="chore-card-title-row">
          <span className="chore-card-icon">{category?.icon || '📌'}</span>
          <span className={`chore-card-title ${chore.status === 2 ? 'chore-card-done' : ''}`}>
            {chore.title}
          </span>
          {chore.isRecurring && <span className="chore-badge chore-badge-recurring" title={t('chores.card.recurring')}>🔄</span>}
          <span
            className="chore-badge chore-badge-priority"
            style={{ color: PRIORITY_COLORS[chore.priority] }}
            title={[t('chores.priority.low'), t('chores.priority.medium'), t('chores.priority.high')][chore.priority]}
          >
            {chore.priority === 2 ? '🔴' : chore.priority === 1 ? '🟡' : '⚪'}
          </span>
        </div>
        <div className="chore-card-meta">
          {roomLabel && (
            <span className="chore-meta-item" title={t('chores.createModal.room')}>
              {room.icon || '🏠'} {roomLabel}
            </span>
          )}
          {chore.estimatedMinutes != null && (
            <span
              className={`chore-meta-item chore-meta-effort ${EFFORT_META[effortBucket].className}`}
              title={t(`chores.effort.${effortBucket}`)}
            >
              {EFFORT_META[effortBucket].icon} {formatMinutes(chore.estimatedMinutes, t)}
            </span>
          )}
          {totalItems > 0 && (
            <span className="chore-meta-item" title={t('chores.card.checklistProgress')}>
              ☑ {completedItems}/{totalItems}
            </span>
          )}
          {chore.dueDate && (
            <span className={`chore-meta-item ${isOverdue ? 'chore-meta-overdue' : ''} ${isDueSoon ? 'chore-meta-due-soon' : ''}`}>
              📅 {new Date(chore.dueDate).toLocaleDateString()}
              {isOverdue && ` ⚠️ ${t('chores.card.overdue')}`}
              {isDueSoon && ` ⏰ ${t('chores.card.soon')}`}
            </span>
          )}
          {chore.status === 1 && assigneeName && (
            <span
              className={`chore-meta-item ${wasAssigned ? 'chore-meta-assigned' : ''}`}
              title={wasAssigned ? t('chores.card.assignedBy', { name: assignerName }) : t('chores.card.selfClaimed')}
            >
              {wasAssigned ? '📌' : '🙋'} {assigneeName}
            </span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="chore-card-body">
          {chore.description && <p className="chore-card-description">{chore.description}</p>}

          {totalItems > 0 && (
            <div className="chore-checklist">
              <div className="chore-checklist-progress">
                <div className="chore-progress-bar">
                  <div className="chore-progress-fill" style={{ width: `${totalItems > 0 ? (completedItems / totalItems) * 100 : 0}%` }}></div>
                </div>
                <span>{completedItems}/{totalItems} {t('chores.card.done')}</span>
              </div>
              {checklistItems.map(item => (
                <div key={item.id} className={`chore-checklist-item ${item.isCompleted ? 'checked' : ''}`}>
                  <label>
                    <input
                      type="checkbox"
                      checked={item.isCompleted}
                      onChange={() => onToggleChecklistItem(chore.id, item.id, !item.isCompleted)}
                    />
                    <span>{item.title}</span>
                  </label>
                  {item.isCompleted && (
                    <span className="chore-checklist-done-info">
                      {item.completedByUser?.name && `${item.completedByUser.name} · `}
                      {item.completedAt && new Date(item.completedAt).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {onDeleteChecklistItem && chore.status !== 2 && (
                    <button
                      className="chore-btn chore-btn-danger chore-btn-tiny"
                      onClick={() => onDeleteChecklistItem(chore.id, item.id)}
                      title={t('chores.card.deleteStep')}
                    >✕</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {chore.status !== 2 && (
            <form className="chore-add-item" onSubmit={handleAddItem}>
              <input
                type="text"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                placeholder={t('chores.card.addChecklistItem')}
                className="chore-add-item-input"
              />
              <button type="submit" className="chore-btn chore-btn-small" disabled={!newItemTitle.trim()}>+</button>
            </form>
          )}

          <div className="chore-card-actions">
            {onClaim && <button className="chore-btn chore-btn-primary chore-btn-small" onClick={onClaim}>🙋 {t('chores.card.claim')}</button>}
            {onAssign && members?.length > 0 && (
              <div className="chore-goal-picker-wrapper">
                <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={() => setShowAssignPicker(!showAssignPicker)}>
                  📌 {assigneeName ? t('chores.card.reassign') : t('chores.card.assign')}
                </button>
                {showAssignPicker && (
                  <div className="chore-goal-picker">
                    {members.map(m => (
                      <button
                        key={m.userId}
                        className={`chore-goal-picker-item ${m.userId === chore.claimedByUserId ? 'active' : ''}`}
                        onClick={() => { onAssign(chore.id, m.userId); setShowAssignPicker(false); }}
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {onUnclaim && <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={onUnclaim}>↩ {t('chores.card.unclaim')}</button>}
            {onComplete && <button className="chore-btn chore-btn-success chore-btn-small" onClick={onComplete}>✅ {t('chores.card.complete')}</button>}
            {onReopen && <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={onReopen}>🔄 {t('chores.card.reopen')}</button>}
            {onEdit && <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={onEdit}>✏️ {t('chores.card.edit')}</button>}
            {onAddGoal && !hasGoal && chore.status !== 2 && (
              <div className="chore-goal-picker-wrapper">
                <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={() => setShowGoalPicker(!showGoalPicker)}>
                  🎯 {t('chores.card.plan')}
                </button>
                {showGoalPicker && (
                  <div className="chore-goal-picker">
                    {Object.entries(goalBucketLabels).map(([key, label]) => (
                      <button
                        key={key}
                        className="chore-goal-picker-item"
                        onClick={() => { onAddGoal(chore.id, parseInt(key)); setShowGoalPicker(false); }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {hasGoal && <span className="chore-badge chore-badge-planned">🎯 {t('chores.card.planned')}</span>}
            {onDelete && <button className="chore-btn chore-btn-danger chore-btn-small" onClick={onDelete} aria-label={t('chores.card.deleteStep')}>🗑</button>}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChoreCard;
